// Чистый планировщик сигналов активности без DOM/Alpine. Частота ограничена:
// максимум один запрос за 30 секунд с trailing send последней активности.
// Timer/DOM wiring остаётся в app.js; here только проверяемая логика.

export const ACTIVITY_MIN_INTERVAL_MS = 30_000;
export const ACTIVITY_ENDPOINT = "/api/auth/activity";

// Только trusted foreground user events считаются активностью. Polling,
// visibilitychange и timer без нового события активностью не являются.
export const TRUSTED_ACTIVITY_EVENTS = ["keydown", "pointerdown", "input", "scroll"];

export function isTrustedActivityEvent(eventName) {
  return TRUSTED_ACTIVITY_EVENTS.includes(eventName);
}

/**
 * Решает, считается ли DOM-событие реальной активностью пользователя.
 *
 * Требуются одновременно: `event.isTrusted === true` (событие создано самим
 * браузером, а не скриптом) и видимая страница. Синтетические события из
 * `dispatchEvent` не доверенные и сигнал не продлевают.
 */
export function shouldTrackActivityEvent(event, isVisible = () => true) {
  if (!event || event.isTrusted !== true) return false;
  if (typeof isVisible === "function" && isVisible() === false) return false;
  return isTrustedActivityEvent(event.type);
}

/**
 * Создаёт планировщик активности.
 *
 * `send` вызывается при фактической отправке. `now`, `setTimer`, `clearTimer` и
 * `isVisible` инъектируются, чтобы unit-тест был детерминированным без реального
 * времени и DOM. Видимость проверяется и при регистрации события, и в trailing
 * отправке: скрытая страница сигнал не продлевает.
 */
export function createActivityScheduler(options = {}) {
  const {
    send,
    now = () => Date.now(),
    setTimer = (callback, delay) => setTimeout(callback, delay),
    clearTimer = (handle) => clearTimeout(handle),
    minIntervalMs = ACTIVITY_MIN_INTERVAL_MS,
    isVisible = () => true,
  } = options;

  if (typeof send !== "function") {
    throw new Error("createActivityScheduler requires a send function");
  }

  let lastSentAt = Number.NEGATIVE_INFINITY;
  let trailingTimer = null;
  let stopped = false;

  function visible() {
    try {
      return isVisible() !== false;
    } catch (_error) {
      return false;
    }
  }

  function fire() {
    trailingTimer = null;
    if (stopped || !visible()) return undefined;
    lastSentAt = now();
    const result = send();
    if (result && typeof result.catch === "function") {
      // 401 уже обработан централизованно в apiFetch; здесь гасим только
      // unhandled rejection и никогда не повторяем mutation автоматически.
      result.catch(() => {});
    }
    return result;
  }

  return {
    /**
     * Регистрирует trusted-событие. Первое событие при отсутствии недавнего
     * сигнала отправляется сразу; последующие в окне — trailing-таймером.
     * Скрытая страница событие игнорирует.
     */
    record() {
      if (stopped || !visible()) return undefined;
      const elapsed = now() - lastSentAt;
      if (trailingTimer === null && elapsed >= minIntervalMs) {
        return fire();
      }
      if (trailingTimer === null) {
        const delay = Math.max(0, minIntervalMs - elapsed);
        trailingTimer = setTimer(() => {
          fire();
        }, delay);
      }
      return undefined;
    },

    /** Останавливает scheduler и отменяет trailing timer (logout/expiry/401). */
    stop() {
      stopped = true;
      if (trailingTimer !== null) {
        clearTimer(trailingTimer);
        trailingTimer = null;
      }
    },

    hasPendingSend() {
      return trailingTimer !== null;
    },

    isStopped() {
      return stopped;
    },
  };
}
